import { yupResolver } from '@hookform/resolvers/yup';
import classNames from 'classnames';
import { format } from 'date-fns';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { useEffect, useState } from 'react';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import toast from 'react-hot-toast';

import {
  EditFieldBasicInfo,
  EditFieldCrop,
  PrintFieldQr,
  ViewBasicInfo,
  ViewCrop,
} from '@/modules/settings/property';
import {
  useGetPropertyFieldDetails,
  useUpdateFieldDetails,
} from '@/modules/settings/property/apiHooks';
import ArchiveButton from '@/modules/settings/property/components/Fields/shared/ArchiveField/ArchiveButton';
import { usePropertySettings } from '@/modules/settings/property/components/logic';
import { IPropertySettings } from '@/modules/settings/property/types';
import { GeneralTabHeader } from '@/modules/settings/shared/components/GeneralTabHeader';
import { GeneralTabSection } from '@/modules/settings/shared/components/GeneralTabSection';
import { Button, Typography } from '@/shared/components/atoms';
import { farmFields } from '@/shared/constant/general';
import routes from '@/shared/constant/routes';
import { useModal } from '@/shared/hooks/context/modalContext/useModal';

const FieldDetails = () => {
  const { t } = useTranslation();

  const { openModal } = useModal();

  const [edit, setEdit] = useState(false);

  const { query, push } = useRouter();

  const propertyId = query?.id ? Number(query.id) : undefined;

  const fieldId = query.fieldId ? Number(query.fieldId) : undefined;

  const { data } = useGetPropertyFieldDetails({
    propertyId,
    fieldId,
  });

  const isFarm = farmFields.some((field) => field === data?.propertyField.type);

  const { createEditPropertyFieldSchema, createEditPackhouseFieldSchema } =
    usePropertySettings();

  const formMethods = useForm<IPropertySettings.ICreateEditFieldFormValues>({
    resolver: yupResolver(
      isFarm ? createEditPropertyFieldSchema : createEditPackhouseFieldSchema
    ),
    mode: 'all',
  });

  useEffect(() => {
    if (data) {
      const { name, type, area, areaUnit, plantationDate, cropId, varietyId } =
        data.propertyField;
      formMethods.reset({
        name,
        type,
        area,
        areaUnit,
        plantationDate: plantationDate ?? undefined,
        cropId,
        varietyId,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const { mutate } = useUpdateFieldDetails();

  const onSubmit: SubmitHandler<
    IPropertySettings.ICreateEditFieldFormValues
  > = (values) => {
    const { areaUnit: _, ...rest } = values;
    if (propertyId && fieldId)
      mutate(
        { ...rest, entityId: propertyId, fieldId: fieldId },
        {
          onSuccess: () => {
            setEdit(false);
            toast.success('Field updated successfully');
          },
        }
      );
  };
  const isActive = data?.propertyField.status === 'ACTIVE';

  return data ? (
    <FormProvider {...formMethods}>
      <form onSubmit={formMethods.handleSubmit(onSubmit)}>
        <GeneralTabHeader
          edit={edit}
          canEdit={isActive}
          setEdit={setEdit}
          editVariant='outlined'
          header={
            <div className='flex items-center gap-16'>
              <Typography variant='h1' weight='semiBold'>
                {data.propertyField.name}
              </Typography>
              <Typography
                variant='caption'
                weight='semiBold'
                className={classNames(
                  'flex h-24 w-56 items-center justify-center rounded-lg',
                  isActive
                    ? 'bg-secondary-90 text-green-300'
                    : 'bg-neutralVariant-95 text-surface-10'
                )}
              >
                {t(isActive ? 'active' : 'archived')}
              </Typography>
            </div>
          }
          title={`${t('Date Created')}: ${format(
            new Date(data.propertyField.createdAt),
            'dd/MM/yyyy'
          )}`}
          actions={
            <div className='flex items-center gap-16'>
              <Button
                className='flex h-56 w-125 items-center justify-center gap-8 !rounded-xl'
                size='none'
                onClick={() =>
                  openModal(<PrintFieldQr field={data.propertyField} />)
                }
              >
                {t('Print QR Code')}
              </Button>
              <ArchiveButton
                fieldId={data.propertyField.id}
                propertyId={propertyId}
                onArchive={() => {
                  push({
                    pathname: routes.propertySettings,
                    query: {
                      tab: 3,
                    },
                  });
                  toast.success(t('Field Archived'));
                }}
              />
            </div>
          }
        />
        <GeneralTabSection title={t('Basic Info')}>
          {edit ? (
            <EditFieldBasicInfo isFarmField={isFarm} />
          ) : (
            <ViewBasicInfo field={data.propertyField} />
          )}
        </GeneralTabSection>
        {isFarm && (
          <GeneralTabSection title={t('crop')}>
            {edit ? (
              <EditFieldCrop hasOperations={data.propertyField.hasOperations} />
            ) : (
              <ViewCrop field={data.propertyField} />
            )}
          </GeneralTabSection>
        )}
      </form>
    </FormProvider>
  ) : null;
};

export default FieldDetails;
